import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Modal, ScrollArea } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useViewport } from '../hooks/useViewport';

/** Named widths, so a screen picks intent rather than a pixel count. */
export type AppModalWidth = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Caps chosen against real content rather than a scale.
 *
 *   sm  — a single decision (language, confirm)
 *   md  — one column of fields (location, warehouse)
 *   lg  — two columns of fields (planning, adjustment)
 *   xl  — two columns plus a table beside them (item master)
 *
 * `min(…, 94vw)` is what makes these responsive without a media query: the cap
 * applies on a wide monitor and the viewport wins on a narrow one, so a 1440p
 * screen stops leaving six inches of dead space either side while a phone still
 * gets edge-to-edge.
 */
const WIDTHS: Record<AppModalWidth, string> = {
  sm: 'min(420px, 94vw)',
  md: 'min(680px, 94vw)',
  lg: 'min(1100px, 94vw)',
  xl: 'min(1440px, 94vw)',
};

interface Props {
  opened: boolean;
  onClose: () => void;
  title: ReactNode;
  width?: AppModalWidth;
  children: ReactNode;
}

/**
 * The application's modal: as wide as the screen allows, never taller than the viewport, and
 * draggable by its header.
 *
 * **Why draggable.** Most of these customers run two monitors and routinely need to read
 * something behind the dialog — an order, another item, a printed pick list on the other screen.
 * A fixed centred modal forces a cancel-and-reopen cycle, losing whatever was typed. Dragging is
 * the cheapest fix and it is what desktop software has always done.
 *
 * **Why the height cap.** The item form ran nearly the full height of a 1440p monitor, which
 * makes the save buttons a scroll away and the whole form hard to scan. `85vh` plus an internal
 * scroll area keeps the header and footer in place regardless of how much the form grows.
 */
export function AppModal({ opened, onClose, title, width = 'md', children }: Props) {
  const { t } = useTranslation();
  const formFactor = useViewport();

  // Dragging is pointless where the modal already fills the screen — there is nothing beside it
  // to reveal — and on a touch device the gesture competes with scrolling the form.
  const draggable = formFactor === 'desktop';

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragFrom = useRef<{ pointerX: number; pointerY: number; startX: number; startY: number } | null>(null);
  // The drag start reads the offset through a ref rather than the closure. Mantine's Modal.Header
  // does not forward a ref, so there is no way to bind a native listener to it — the handler has
  // to be the React one, and reading current state through a ref keeps it correct regardless of
  // when React re-created that handler.
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  // Re-centre on each open. A modal that reappears where it was last dragged looks misplaced
  // when it is a different record, and one dragged nearly off-screen would come back unreachable.
  useEffect(() => {
    if (opened) setOffset({ x: 0, y: 0 });
  }, [opened]);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const from = dragFrom.current;
    if (!from) return;
    setOffset(
      clampToViewport({
        x: from.startX + (event.clientX - from.pointerX),
        y: from.startY + (event.clientY - from.pointerY),
      }),
    );
  }, []);

  const onPointerUp = useCallback(() => {
    dragFrom.current = null;
  }, []);

  useEffect(() => {
    if (!draggable) return;
    // Listeners on the window, not the header: a fast drag outruns the pointer and would
    // otherwise leave the modal stuck to the cursor after the button was released outside it.
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [draggable, onPointerMove, onPointerUp]);

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (!draggable) return;
    // Only the header's own background starts a drag. Without this the close button would begin
    // one, so clicking the X would jitter the dialog on its way out.
    if ((event.target as HTMLElement).closest('button')) return;
    dragFrom.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      startX: offsetRef.current.x,
      startY: offsetRef.current.y,
    };
  }

  /**
   * Keyboard nudging, because a drag handle reachable only by pointer is unusable for anyone on a
   * keyboard — and this is a warehouse product where a mouse is often not to hand.
   */
  function onHeaderKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!draggable) return;
    const step = event.shiftKey ? 60 : 15;
    const moves: Record<string, { x: number; y: number }> = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setOffset((o) => clampToViewport({ x: o.x + move.x, y: o.y + move.y }));
      return;
    }
    // Home returns it to centre, so a modal dragged somewhere awkward is one key from usable.
    if (event.key === 'Home') {
      event.preventDefault();
      setOffset({ x: 0, y: 0 });
    }
  }

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      size={WIDTHS[width]}
      centered
      // Internal scrolling rather than the page scrolling behind the overlay: the header stays
      // put, so the title and close button never scroll away from a long form.
      scrollAreaComponent={ScrollArea.Autosize}
      // The drag offset goes on the INNER wrapper, as a transform. Two earlier attempts got this
      // wrong in ways worth recording:
      //
      //   transform on Modal.Content — silently overwritten. Mantine's open/close transition
      //     writes `transform` on that element, so the modal never moved at all.
      //   left/top on Modal.Content — moved it at DOUBLE the pointer speed. The content is a flex
      //     item in a centred, scrollable container, so shifting it also shifts what the container
      //     centres on, and the two add up. Measured: a 100px pointer move displaced it 200px.
      //
      // The inner wrapper is full-viewport, is not what the transition animates, and a transform
      // on it affects no layout — so the dialog tracks the pointer exactly.
      styles={{ inner: { transform: `translate(${offset.x}px, ${offset.y}px)` } }}
    >
      <Modal.Overlay />
      <Modal.Content style={{ maxHeight: '85vh' }}>
        <Modal.Header
          onPointerDown={startDrag}
          onKeyDown={onHeaderKeyDown}
          tabIndex={draggable ? 0 : undefined}
          role={draggable ? 'button' : undefined}
          aria-label={draggable ? t('shell.modalDragHandle') : undefined}
          style={draggable ? { cursor: dragFrom.current ? 'grabbing' : 'grab', touchAction: 'none' } : undefined}
        >
          <Modal.Title>{title}</Modal.Title>
          {/* Labelled so it is reachable by name for a screen reader — and so the drag handler's
              "ignore clicks on buttons" rule has something identifiable to test against. */}
          <Modal.CloseButton aria-label={t('shell.close')} />
        </Modal.Header>
        <Modal.Body>{children}</Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

/**
 * Keeps enough of the dialog on screen to grab it again. Without this a drag can put the header
 * past the edge, and the only way back is to reload — the header is the handle, so losing it
 * loses the ability to move it back.
 */
function clampToViewport(offset: { x: number; y: number }): { x: number; y: number } {
  if (typeof window === 'undefined') return offset;
  // A non-finite offset would render as no offset at all and leave the modal apparently stuck,
  // with the stored state out of step with what is on screen. Only reachable from an event with
  // no coordinates, which a real browser does not produce — but the failure is confusing enough
  // to be worth one line.
  const x = Number.isFinite(offset.x) ? offset.x : 0;
  const y = Number.isFinite(offset.y) ? offset.y : 0;
  const limitX = window.innerWidth / 2;
  const limitY = window.innerHeight / 2;
  return {
    x: Math.max(-limitX, Math.min(limitX, x)),
    y: Math.max(-limitY, Math.min(limitY, y)),
  };
}
